import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {
    isBiometricAvailable,
    getBiometricLabel,
    setBiometricOptIn,
    markBiometricOptInAsked,
} from '../../platform/biometric';

// One-time prompt asking the user whether to enable biometric login.
// Shown ONCE, before the main app boots. The `onDone` callback should flip
// a parent flag so subsequent launches skip this screen.

const BiometricOptInScreen = ({ onDone }) => {
    const [label, setLabel] = useState('Face ID');
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const available = await isBiometricAvailable();
            if (!available) {
                // No biometry on this device — skip silently.
                await markBiometricOptInAsked();
                if (!cancelled) onDone();
                return;
            }
            const l = await getBiometricLabel();
            if (cancelled) return;
            setLabel(l);
            setReady(true);
        })();
        return () => { cancelled = true; };
    }, [onDone]);

    const handleEnable = async () => {
        await setBiometricOptIn(true);
        await markBiometricOptInAsked();
        onDone();
    };

    const handleSkip = async () => {
        await setBiometricOptIn(false);
        await markBiometricOptInAsked();
        onDone();
    };

    if (!ready) {
        return (
            <Box style={{ position: 'fixed', inset: 0, background: '#0E1524' }} />
        );
    }

    return (
        <Box
            style={{
                minHeight: '100vh',
                background: '#0E1524',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: 'env(safe-area-inset-top, 0px)',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
        >
            <Box
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 32,
                }}
            >
                <Box
                    style={{
                        width: 96,
                        height: 96,
                        borderRadius: 48,
                        background: 'rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 32,
                    }}
                >
                    <LockIcon style={{ color: '#FFFFFF', fontSize: 48 }} />
                </Box>
                <Box style={{ color: '#FFFFFF', fontSize: 26, fontWeight: 700, marginBottom: 12, textAlign: 'center' }}>
                    {`Use ${label} to sign in`}
                </Box>
                <Box style={{ color: 'rgba(255,255,255,0.7)', fontSize: 16, lineHeight: 1.5, textAlign: 'center', maxWidth: 320, marginBottom: 8 }}>
                    {`Sign in faster and more securely with ${label}.`}
                </Box>
                <Box style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, textAlign: 'center', marginBottom: 48 }}>
                    You can change this later in Settings.
                </Box>

                <Box style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={handleEnable}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleEnable(); }}
                        style={{
                            background: '#FFFFFF',
                            color: '#0E1524',
                            fontSize: 16,
                            fontWeight: 600,
                            padding: '16px 24px',
                            borderRadius: 16,
                            textAlign: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            minHeight: 56,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {`Enable ${label}`}
                    </Box>
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={handleSkip}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSkip(); }}
                        style={{
                            background: 'transparent',
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: 16,
                            fontWeight: 600,
                            padding: '16px 24px',
                            borderRadius: 16,
                            border: '2px solid rgba(255,255,255,0.2)',
                            textAlign: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            minHeight: 56,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        Not now
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default BiometricOptInScreen;
